/**
 * Tenant self-service Config Versioning API service.
 *
 * Thin typed wrapper around the origami tenant-scoped `/api/config/*`
 * endpoints (history, version detail, revert, snapshot list/capture/restore).
 * Every call uses `claimflowApiClient`, whose request interceptor injects the
 * Bearer token (`localStorage.claimflow_auth_token`) and `X-Tenant-ID`
 * (`localStorage.claimflow_active_tenant_id`) — exactly the two headers these
 * endpoints require. The caller's role must additionally grant the relevant
 * `config_version.*` / `config_snapshot.*` permission (enforced server-side).
 *
 * Response shapes mirror the origami JSON resources exactly (snake_case). The
 * two *list* endpoints (`GET /config/history`, `GET /config/snapshots`) are not
 * wrapped here — they are consumed by the integrated `DataList` in server mode
 * (its built-in `useFetchListData` React Query, using the same client), so this
 * service only carries the four non-list operations. See
 * `../configs/configHistoryListConfig` / `configBackupsListConfig`.
 *
 * Backend contract: origami `ITEM-20260705-1227-cvap` — "Item E (kusudama)
 * tenant-facing API contract".
 */

import { claimflowApiClient } from './claimflowApiClient';

// ---------------------------------------------------------------------------
// Tracked entity types (history filter dropdown)
// ---------------------------------------------------------------------------

/**
 * The eight tracked `entity_type` values the tenant history endpoint accepts.
 * Sending any other value (or an empty string) 422s server-side, so the
 * history filter dropdown offers exactly these — the empty "all" option is
 * omitted from the request by the DataList param builder.
 */
export const TRACKED_ENTITY_TYPES = [
  'module_tenant_config',
  'module_tenant_role_config',
  'module_user_config',
  'tenant_settings',
  'component_prop_label',
  'component_rule',
  'workflow_rule',
  'workflow_definition',
] as const;

export type TrackedEntityType = (typeof TRACKED_ENTITY_TYPES)[number];

/** Human-readable French labels for each tracked entity type. */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  module_tenant_config: 'Config module (locataire)',
  module_tenant_role_config: 'Config module (rôle)',
  module_user_config: 'Config module (utilisateur)',
  tenant_settings: 'Paramètres du locataire',
  component_prop_label: 'Libellé de propriété',
  component_rule: 'Règle de composant',
  workflow_rule: 'Règle de workflow',
  workflow_definition: 'Définition de workflow',
};

/** Resolve a display label for an entity type, falling back to the raw slug. */
export function entityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}

// ---------------------------------------------------------------------------
// DTOs — match origami's JsonResource output (snake_case)
// ---------------------------------------------------------------------------

/** Actor summary eager-loaded on the history / version endpoints. */
export interface ConfigActor {
  id: number;
  name: string;
  email: string;
}

/** Mirrors origami `ConfigVersionResource`. */
export interface ConfigVersion {
  id: string;
  tenant_id: string | null;
  entity_type: string;
  entity_key: string;
  entity_id: string | null;
  version: number;
  action: 'created' | 'updated' | 'deleted';
  snapshot: Record<string, unknown> | null;
  checksum: string;
  actor_id: number | null;
  actor_type: 'user' | 'system' | 'seeder' | 'import';
  source: 'api' | 'seeder' | 'import' | 'revert' | 'system';
  actor: ConfigActor | null;
  created_at: string;
}

/** Mirrors origami `ConfigSnapshotResource` (list omits the heavy `document`). */
export interface ConfigSnapshot {
  id: string;
  tenant_id: string;
  label: string | null;
  kind: 'manual' | 'scheduled' | 'pre_seed' | 'pre_import' | 'pre_restore';
  checksum: string;
  entity_counts: Record<string, number>;
  schema_version: number;
  created_by: number | null;
  exported_path: string | null;
  created_at: string;
  document?: Record<string, unknown>;
}

/** A single field-level change inside a {@link VersionDiff}. */
export interface VersionDiffChange {
  field: string;
  from: unknown;
  to: unknown;
}

/**
 * Mirrors origami `VersionDiff`. `from` is the current live value, `to` is the
 * value after reverting. `changes` is empty when the target is identical.
 */
export interface VersionDiff {
  is_current: boolean;
  current_version: number;
  target_version: number;
  changes: VersionDiffChange[];
}

/** A restored entity line inside a {@link RestoreReport}. */
export interface RestoreReportEntry {
  entity_type: string;
  entity_key: string;
  action?: 'created' | 'updated' | 'unchanged' | 'deleted';
  reason?: string;
}

/** Mirrors origami `RestoreReport` (the `data` of restore / `report` of revert). */
export interface RestoreReport {
  dry_run: boolean;
  restored: RestoreReportEntry[];
  skipped: RestoreReportEntry[];
  conflicts: RestoreReportEntry[];
  counts: Record<
    string,
    { created: number; updated: number; unchanged: number; deleted: number }
  >;
}

/** Payload returned by `POST /config/versions/{id}/revert`. */
export interface RevertResult {
  dry_run: boolean;
  entity_type: string;
  entity_key: string;
  target_version: number;
  diff: VersionDiff;
  /** Present only when `dry_run === false` (a real revert was applied). */
  report?: RestoreReport;
}

/** Payload returned by `GET /config/versions/{id}`. */
export interface VersionDetail {
  data: ConfigVersion;
  diff: VersionDiff;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Typed wrapper over the four non-list tenant config-versioning endpoints.
 * The two list endpoints are handled by DataList server mode (see module
 * docblock).
 */
export const configVersioningService = {
  /** `GET /config/versions/{id}` — version snapshot + diff vs current. */
  async fetchVersion(versionId: string): Promise<VersionDetail> {
    const { data } = await claimflowApiClient.get<VersionDetail>(
      `/config/versions/${encodeURIComponent(versionId)}`,
    );
    return data;
  },

  /**
   * `POST /config/versions/{id}/revert` — reverts the entity to the selected
   * version. `dryRun: true` returns the diff only (no `report`); `dryRun:
   * false` applies the change and returns a `report`.
   */
  async revertVersion(
    versionId: string,
    dryRun: boolean,
  ): Promise<RevertResult> {
    const { data } = await claimflowApiClient.post<{ data: RevertResult }>(
      `/config/versions/${encodeURIComponent(versionId)}/revert`,
      { dry_run: dryRun },
    );
    return data.data;
  },

  /** `POST /config/snapshots` — capture a manual snapshot (201). */
  async createSnapshot(label?: string): Promise<ConfigSnapshot> {
    const body = label && label.trim() !== '' ? { label: label.trim() } : {};
    const { data } = await claimflowApiClient.post<{ data: ConfigSnapshot }>(
      '/config/snapshots',
      body,
    );
    return data.data;
  },

  /**
   * `POST /config/snapshots/{id}/restore` — restore a snapshot (merge-only;
   * any prune is ignored server-side). `dryRun: true` previews the report
   * without writing.
   */
  async restoreSnapshot(
    snapshotId: string,
    dryRun: boolean,
  ): Promise<RestoreReport> {
    const { data } = await claimflowApiClient.post<{ data: RestoreReport }>(
      `/config/snapshots/${encodeURIComponent(snapshotId)}/restore`,
      { dry_run: dryRun },
    );
    return data.data;
  },
};
